DataDefinitionRequesterFactory.$inject = [
	'$q',
	'mx.SolutionBuilderAgent.DataQueryService',
	'mx.SolutionBuilderAgent.SchemaService',
	'mx.shell.ObjectMapper',
	'mx.sb.Agent.DataQuery.BaseRequester',
	'mx.sb.Agent.DataQuery.DataDefinitionRequestOptions',
	'mx.sb.Agent.DataQuery.Enums',
];

export default function DataDefinitionRequesterFactory($q, dataQueryService, schemaService, objectMapper, BaseRequester, DataDefinitionRequestOptions, DataQueryEnums) {
	const ColumnSortingEnum = DataQueryEnums.ColumnSortingEnum;

	/**
	 * @ngdoc Class
	 * @name mx.sb.Agent.DataQuery.DataDefinitionRequester
	 *
	 * @description
	 * Class DataDefinition Requester
	 *
	 */

	function DataDefinitionRequester(dataQuery) {
		BaseRequester.call(this, dataQuery);
		this.schemaService = schemaService;

		this.options = new DataDefinitionRequestOptions();

		this.supportedPreprocessors = {
			search: true,
			paging: true,
			sorting: true,
			columnSearch: true,
			advanceSearch: true,
		};
	}

	DataDefinitionRequester.prototype = angular.extend(Object.create(BaseRequester.prototype), {
		load: function (preprocessors) {
			if (this.options.returnNoItems) {
				return $q.when({
					dataItems: [],
					totalItems: 0,
				});
			}

			return dataQueryService.getRelationItemsWithTotalCount(this.prepareRequest(preprocessors)).then(({ items, totalCount }) => {
				return $q.all(items.map((rawEntity) => this.createRow(objectMapper.mapItem(rawEntity)))).then((rows) => {
					return {
						dataItems: rows,
						totalItems: totalCount,
					};
				});
			});
		},

		getEntityTypes: async function () {
			const types = await this.schemaService.getTypeItems(undefined, this.dataQuery.SchemaClassName);
			return types.map((entityType) => {
				return {
					_displayName: entityType.DisplayName,
					_type: entityType.Name,
					_itemType: 'CI',
				};
			});
		},

		_getDisplayEntity: function (rawEntity) {
			return $q.when(rawEntity);
		},

		applyPreprocessors(request, preprocessors) {
			if (preprocessors) {
				if (preprocessors.searchPreprocessor) {
					request.search = preprocessors.searchPreprocessor.keyword;
				}

				if (preprocessors.sortingPreprocessor) {
					var sortedColumns = preprocessors.sortingPreprocessor.sortedColumns;

					if (!Array.isArray(sortedColumns) || !sortedColumns.length) {
						request.orderBy = '';
					} else {
						request.orderBy = sortedColumns.reduce(function (accumulator, column, index) {
							var direction = column.Sorting === ColumnSortingEnum.Ascending ? 'asc' : 'desc';
							return accumulator + (index > 0 ? ',' : '') + column.Name + ' ' + direction;
						}, '');
					}
				}

				if (preprocessors.pagingPreprocessor) {
					request.pageNumber = preprocessors.pagingPreprocessor.page + 1; //TODO: refactor dataQueryService.getRelationsItems and remove " + 1 ";
					request.pageSize = preprocessors.pagingPreprocessor.pageSize;
				}

				request.additionalFilter = {
					Conditions: [],
					Groups: [],
					LogicalOperator: mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND,
				};

				if (preprocessors.columnSearchPreprocessor) {
					request.additionalFilter.Groups.push(preprocessors.columnSearchPreprocessor.conditions);
				}

				if (preprocessors.advanceSearchPreprocessor) {
					request.additionalFilter.Groups.push(preprocessors.advanceSearchPreprocessor.conditions);
					if (preprocessors.advanceSearchPreprocessor.filters.length) {
						request.filters = request.filters.length > 0 ? request.filters + ',' : request.filters;
						request.filters += preprocessors.advanceSearchPreprocessor.filters.join(',');
					}
				}
			}
			return request;
		},

		prepareRequest: function (preprocessors) {
			const request = {
				filter: this.options.filter,
				allowedTypes: this.options.allowedTypes,

				entityClassName: this.options.entityClassName,
				relationName: this.options.relationName,
				addedRelations: this.options.objectIDs,
				removedRelations: this.options.excludedIDs,
				useObjectId: this.options.useObjectId,

				dataQueryName: this.options.columnDefinition,

				filters: this.options.filters || '',
				fragmentId: this.options.fragmentId,
				archived: this.options.archived,

				columns: this.dataQuery.Columns.filter((column) => column.visible)
					.map((column) => column.Name)
					.join(','),

				lookupFor: this.options.lookupFor,
				resetFilter: this.options.resetFilter,
			};

			this.applyPreprocessors(request, preprocessors);

			return request;
		},

		getTotalItems: function (preprocessors) {
			const request = this.prepareRequest(preprocessors);
			delete request.pageNumber;
			delete request.pageSize;

			//TODO: getRelationItemsCount doesn't support filters
			return dataQueryService.getRelationItemsCount(request);
		},

		exportData: function (preprocessors) {
			const preprocessorsForExport = Object.assign({}, preprocessors);
			delete preprocessorsForExport.pagingPreprocessor;
			return dataQueryService.exportRelationItems(this.prepareRequest(preprocessorsForExport));
		},

		//TODO: remove it and use _id for all data types
		entitiesEqualityFunction: function (itemA, itemB) {
			return (itemA && itemB && itemA.Id === itemB.Id) || itemA === itemB;
		},

		loadEntityById: function (itemId) {
			const request = this.prepareRequest();
			delete request.addedRelations;
			delete request.removedRelations;
			delete request.lookupFor;

			request.objectId = itemId;

			return dataQueryService.getRelationItems(request).then(
				function (items) {
					if (items.length) {
						return objectMapper.mapItem(items[0]);
					} else {
						return $q.reject();
					}
				}.bind(this)
			);
		},

		isEntityCorrect: function (entity) {
			if (!this.options.correctEntityFilter) {
				return $q.resolve(true);
			}
			const request = this.prepareRequest();
			delete request.addedRelations;
			delete request.removedRelations;
			delete request.lookupFor;

			request.filter = this.options.correctEntityFilter;
			request.objectId = entity._id;

			return dataQueryService
				.getRelationItems(request)
				.then(
					function (items) {
						return items && items.length === 1;
					}.bind(this)
				)
				.catch(function () {
					return false;
				});
		},
	});

	return DataDefinitionRequester;
}
